export type ConsentState = "pending" | "denied" | "allowed";
export type TCData = {
  cmpStatus?: string; eventStatus?: string; gdprApplies?: boolean; tcString?: string;
  purpose?: { consents?: Record<number, boolean>; legitimateInterests?: Record<number, boolean> };
  vendor?: { consents?: Record<number, boolean>; legitimateInterests?: Record<number, boolean> };
  publisher?: { restrictions?: Record<number, Record<number, number>> };
};
export function tcfPermission(data: TCData | undefined, success: boolean): ConsentState {
  if (!success || !data || data.cmpStatus === "error") return "denied";
  if (data.cmpStatus !== "loaded" || !["tcloaded", "useractioncomplete"].includes(data.eventStatus ?? "") || typeof data.gdprApplies !== "boolean") return "pending";
  if (data.gdprApplies === false) return "allowed";
  const google = 755;
  if (typeof data.tcString !== "string" || !data.tcString.trim() || data.purpose?.consents?.[1] !== true || data.vendor?.consents?.[google] !== true) return "denied";
  for (const purpose of [1, 2, 7, 9, 10]) {
    const restriction = data.publisher?.restrictions?.[purpose]?.[google];
    if (restriction === 0 || purpose === 1 && restriction === 2) return "denied";
    if (restriction !== undefined && ![0, 1, 2].includes(restriction)) return "pending";
    const consent = data.purpose.consents?.[purpose] === true && data.vendor.consents?.[google] === true;
    const interest = data.purpose.legitimateInterests?.[purpose] === true && data.vendor.legitimateInterests?.[google] === true;
    if (purpose === 1 || restriction === 1) { if (!consent) return "denied"; }
    else if (restriction === 2) { if (!interest) return "denied"; }
    else if (!consent && !interest) return "denied";
  }
  return "allowed";
}
export type GppData = {
  cmpStatus?: string; signalStatus?: string; applicableSections?: number[];
  supportedAPIs?: string[]; parsedSections?: Record<string, Record<string, unknown>[]>;
};
// IAB GPP section registry, IDs 7–27. Unknown frameworks remain unresolved.
const usSections = ["usnat", "usca", "usva", "usco", "usut", "usct", "usfl", "usmt", "usor", "ustx", "usde", "usia", "usne", "usnh", "usnj", "ustn", "usmn", "usmd", "usin", "usky", "usri"];
export function gppPermission(data: GppData | undefined, success: boolean): ConsentState {
  if (!success || data?.cmpStatus === "error") return "denied";
  if (!data || data.cmpStatus !== "loaded" || data.signalStatus !== "ready" || !Array.isArray(data.applicableSections) || !data.applicableSections.length) return "pending";
  if (data.applicableSections.length === 1 && data.applicableSections[0] === -1) return "allowed";
  for (const id of data.applicableSections) {
    if (!Number.isInteger(id)) return "pending";
    const prefix = usSections[id - 7];
    if (!prefix || !Array.isArray(data.supportedAPIs) || !data.supportedAPIs.includes(`${id}:${prefix}`)) return "pending";
    const sections = data.parsedSections?.[prefix];
    if (!Array.isArray(sections) || !sections.length || !sections.every(section => section !== null && typeof section === "object" && !Array.isArray(section)) || !sections.some(section => "SaleOptOut" in section)) return "pending";
    for (const section of sections) for (const [key, value] of Object.entries(section)) {
      if (key === "Gpc" && value === true) return "denied";
      if (key === "Gpc" && value !== false) return "pending";
      if (key.endsWith("OptOut")) {
        if (value === 1) return "denied";
        if (value !== 0 && value !== 2) return "pending";
      }
    }
  }
  return "allowed";
}
export function combinedPermission(europe: ConsentState, us: ConsentState, globalOptOut = false): ConsentState {
  if (globalOptOut || europe === "denied" || us === "denied") return "denied";
  return europe === "allowed" && us === "allowed" ? "allowed" : "pending";
}
